import { useEffect, useId, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { MotionConfig, motion } from "framer-motion";
import { ArrowRight, Check, Heart, Minus, Plus, Sparkles } from "lucide-react";
import { CLUB_FAQS, CLUB_SCHEDULE } from "@/lib/club";
import { upcomingLives } from "@/lib/schedule";
import { Draw, EASE, HEART, Head, Ink, VIEW, datePart } from "../bits";
import { Part } from "../Page";
import { MembersPage, M, joinCta, useRole, useTo } from "./Members";
import { FOUNDING, PLANS, PROJECTS, type Plan, type Role } from "./sample";
import "../SectionsA.css";
import "./Join.css";

/* Join (/blue-2/members/join): the paywall and sales page for the in-house
   club. Every locked thing in the club links here. Two tiers side by side,
   how a club month works, a proposal for moving patrons over from Patreon,
   and the FAQ. Prototype: join buttons only show a toast where checkout
   would open. */

type Tier = Plan;
const TIERS: { key: Tier; tag: string; pitch: string; yes: string[]; no: string[] }[] = [
  { key: "member", tag: "the whole club",
    pitch: "Sew with me live, catch every replay, and share your makes with the nicest sewists on the internet.",
    yes: ["Two live classes a month, one bigger project", "Every replay, with chapters, any time", "All club posts, polls & behind-the-scenes", "Community makes & monthly challenges", "Member discounts on kits, patterns & merch"], no: [] },
  { key: "friend", tag: "support the fun",
    pitch: "Love the free YouTube lives? Chip in a little to keep them going, and get the club posts.",
    yes: ["Club posts & studio updates", "Thread Friend–only posts", "See & share makes in the community", "Keeping the free YouTube lives going ♥"], no: ["Live classes", "Class replays"] },
];

const MONTH = [
  { when: "Week 1", what: "Project drops", text: "I post the project, the skill level, and the supply list, so you can gather fabric." },
  { when: "2nd Tuesday", what: "Class 1, live", text: "We cut, piece, and work through the tricky bits together. Ask me anything in chat." },
  { when: "That Thursday", what: "Class 2, live", text: "We finish it! Straps, lining, binding, the big reveal. Replays go up the same night." },
  { when: "All month", what: "Share & cheer", text: "Post your make, join the challenge, and cheer each other on. Free YouTube lives most Tuesdays." },
];

const PATREON_STEPS = [
  { t: "Nothing changes yet", d: "Keep your Patreon membership as it is. Classes stay on Patreon until switch day, and we’ll tell you the date a month ahead." },
  { t: "Grab your invite", d: `Every patron gets an email with a personal link. Sign in with the same email and your tier, join date, and ${FOUNDING} badge come with you.` },
  { t: "Cancel Patreon after", d: "Once you’re in, cancel Patreon. If Patreon already billed you that month, your first month here is on me, so nobody pays twice." },
];
const PATREON_PERKS = [`Same price: ${PLANS.member.per} and ${PLANS.friend.per}, no increase`, `A ${FOUNDING} badge for everyone who switches`, "Every Patreon-era replay moves here too"];

// Real club FAQ where it still fits, plus in-house answers (sample copy).
const real = (i: number) => ({ q: CLUB_FAQS[i].question, a: CLUB_FAQS[i].answer });
const FAQS: { q: string; a: ReactNode }[] = [
  real(0),
  { q: "What will we make?", a: "Each month, I’ll walk you through one bigger project in two live classes. Think quilts, clothing, bags, shoes, handmade gifts, and whatever creative rabbit hole catches my eye next. The project post in the club has the details." },
  real(2),
  real(3),
  real(4),
  { q: "What’s a Thread Friend?", a: `It’s the ${PLANS.friend.per} way to support the free YouTube lives. You get the club posts and Thread Friend posts and can share your makes, but not the live classes or replays. You can upgrade to Member whenever you like.` },
  { q: "How do I cancel or switch tiers?", a: "From Settings in the club, any time, in a couple of taps. Billing is monthly. If you cancel, you keep everything until the end of the month you paid for. Switch from Thread Friend to Member and your $3 stops right away." },
  { q: "Is there an app?", a: "No app needed. The club works in your phone’s browser, and you can add it to your home screen so it opens like one. Live classes play right on the class page." },
];

const ROLE_NOTE: Record<string, { text: string; cta?: { to: string; label: string } }> = {
  member: { text: "You’re already a member. Nothing to do here!", cta: { to: M.home, label: "Back to club home" } },
  friend: { text: "You’re a Thread Friend. Upgrade to Member any time; your $3 stops when you do." },
  chelsea: { text: "Creator view: every locked thing in the club sends visitors and Thread Friends here." },
};

/** The tier button words. The Member tier uses the club's one join/upgrade line (joinCta). */
function tierLabel(t: Tier, role: Role) {
  if (t === "member") return joinCta(role, "members")?.label ?? `${PLANS.member.name} · ${PLANS.member.per}`;
  return role === "member" ? `Switch to ${PLANS.friend.name} · ${PLANS.friend.per}` : `Be a ${PLANS.friend.name} · ${PLANS.friend.per}`;
}

function useToast() {
  const [toast, setToast] = useState<{ msg: string; n: number } | null>(null);
  useEffect(() => { if (!toast) return; const t = window.setTimeout(() => setToast(null), 3600); return () => window.clearTimeout(t); }, [toast]);
  return { toast, show: (msg: string) => setToast(t => ({ msg, n: (t?.n ?? 0) + 1 })) };
}

/* ═══ Header ═══ */
function Header({ onJoin }: { onJoin: (t: Tier, role: Role) => void }) {
  const role = useRole();
  const to = useTo();
  const cta = joinCta(role, "members");
  const now = new Date();
  const next = upcomingLives(1, "club", now)[0];
  const sameMonth = !!next && datePart(next.start, { month: "long", year: "numeric" }) === PROJECTS[0].month;
  const tonight = !!next && datePart(next.start, { dateStyle: "short" }) === datePart(now, { dateStyle: "short" });
  return <div className="mj-head">
    <div className="mj-head-copy">
      <motion.p className="mj-kicker" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: .1 }}>Loose Threads Club · {PLANS.member.per} · cancel anytime</motion.p>
      <h1 className="mj-title" aria-label="Pull up a chair">
        <motion.span className="mj-title-hand" aria-hidden="true" initial={{ opacity: 0, rotate: -12 }} animate={{ opacity: 1, rotate: -5 }} transition={{ delay: .35, duration: .5, ease: EASE }}>pull up a</motion.span>
        <span className="mj-title-word" aria-hidden="true">
          {"chair".split("").map((c, i) => <motion.span key={i} initial={{ y: "18%", opacity: 0 }} animate={{ y: "0%", opacity: 1 }} transition={{ delay: .1 + i * .05, duration: .6, ease: EASE }}>{c}</motion.span>)}
        </span>
      </h1>
      <motion.p className="mj-lede" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .6, duration: .45, ease: EASE }}>
        Hi, I’m Chelsea! Sew with me live twice a month, catch every replay, and share your makes with people who get it. Your next project, a little help, and a lot of fun.
      </motion.p>
      <motion.div className="mj-head-actions" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .75, duration: .45, ease: EASE }}>
        {!cta ? <Link to={to(M.home)} className="mb-btn mb-btn-hero">{role === "chelsea" ? "Back to club home" : "You’re in! Club home"} <ArrowRight size={18} aria-hidden="true" /></Link>
          : <span className="mj-ring-wrap">
            <button type="button" className="mb-btn mb-btn-hero" onClick={() => onJoin("member", role)}>{cta.label} <ArrowRight size={18} aria-hidden="true" /></button>
            <svg className="mj-ring" viewBox="0 0 300 110" preserveAspectRatio="none" aria-hidden="true"><Draw d="M210 10 C 120 -4, 20 12, 8 52 C -2 92, 120 106, 220 98 C 300 90, 304 40, 260 18 C 240 8, 214 6, 190 9" delay={1.6} width={3} /></svg>
          </span>}
        <a href="#mj-tiers" className="mb-btn mb-btn-line">Compare the tiers</a>
      </motion.div>
      {next && <p className="mj-next">Next class: <b>{tonight ? "tonight" : datePart(next.start, { weekday: "long", month: "long", day: "numeric" })}</b>, 7 PM MT · {sameMonth ? PROJECTS[0].title : "next month’s project"}</p>}
    </div>
    <div className="mj-head-side">
      <motion.p className="mj-price" initial={{ scale: 0, rotate: -40 }} animate={{ scale: 1, rotate: -12 }} transition={{ delay: 1, type: "spring", stiffness: 220, damping: 13 }}>
        <b>$25</b>a month<small>cancel anytime</small>
      </motion.p>
      <motion.figure className="mj-polaroid" initial={{ opacity: 0, y: 30, rotate: 8 }} animate={{ opacity: 1, y: 0, rotate: 3 }} transition={{ delay: .4, type: "spring", stiffness: 120, damping: 15 }}>
        <i className="b2-snap-tape b2-ktape-strong" aria-hidden="true" />
        <span><img src="/chelsea.JPG" alt="Chelsea in her handmade outfit with a colorful quilted patchwork bag" /></span>
        <figcaption>this month: the {PROJECTS[0].title.toLowerCase()} <b aria-hidden="true">♥</b></figcaption>
      </motion.figure>
    </div>
  </div>;
}

/* ═══ Tiers ═══ */
function Tiers({ onJoin }: { onJoin: (t: Tier, role: Role) => void }) {
  const role = useRole();
  const to = useTo();
  const note = ROLE_NOTE[role];
  return <section id="mj-tiers" className="mj-sec mj-tiers" aria-labelledby="mj-tiers-title">
    <div className="b2-wrap">
      <Head id="mj-tiers-title" hand="pick your" word="spot" tone="blue" className="b2-head-center mb-headsm" />
      <p className="mj-sub">Billed monthly. Cancel anytime from Settings. Prices in USD.</p>
      {note && <p className="mj-rolenote" role="note"><Sparkles size={16} aria-hidden="true" /> {note.text}
        {note.cta && (note.cta.to.startsWith("#") ? <a href={note.cta.to}>{note.cta.label} <ArrowRight size={14} aria-hidden="true" /></a> : <Link to={to(note.cta.to)}>{note.cta.label} <ArrowRight size={14} aria-hidden="true" /></Link>)}</p>}
      <div className="mj-tier-grid">
        {TIERS.map((t, i) => {
          const mine = role === t.key;
          const plan = PLANS[t.key];
          const label = tierLabel(t.key, role === "chelsea" ? "visitor" : role);
          return <motion.article key={t.key} className={`mj-tier mj-tier-${t.key}${mine ? " is-mine" : ""}`} aria-labelledby={`mj-tier-${t.key}`}
            initial={{ opacity: 0, y: 40, rotate: i ? 3 : -3 }} whileInView={{ opacity: 1, y: 0, rotate: i ? .8 : -.8 }} viewport={VIEW} transition={{ delay: i * .12, type: "spring", stiffness: 120, damping: 16 }}>
            <i className={`b2-snap-tape b2-ktape-${i ? "soft" : "pop"}`} aria-hidden="true" />
            {t.key === "member" && <p className="mj-sticker" aria-hidden="true">most<br />loved</p>}
            <p className="mj-tier-tag">{mine ? "Your plan ♥" : t.tag}</p>
            <h3 id={`mj-tier-${t.key}`} className="mj-tier-name">{plan.name}</h3>
            <p className="mj-tier-price"><b>${plan.price}</b><span>/mo</span></p>
            <p className="mj-tier-pitch">{t.pitch}</p>
            <ul className="mj-tier-list">
              {t.yes.map(y => <li key={y}><Check size={18} aria-hidden="true" /> {y}</li>)}
              {t.no.map(n => <li key={n} className="is-no"><Minus size={18} aria-hidden="true" /> <span><span className="mj-sr">Not included: </span>{n}</span></li>)}
            </ul>
            {t.key === "member" && <p className="mj-tier-when">{CLUB_SCHEDULE}</p>}
            {role === "chelsea" ? <p className="mj-tier-chelsea">Visitors see “{label}” here.</p>
              : mine ? <Link to={to(M.settings)} className="mb-btn mb-btn-line mb-btn-wide mj-tier-btn">Manage in Settings <ArrowRight size={16} aria-hidden="true" /></Link>
              : <button type="button" className={`mb-btn mb-btn-${t.key === "member" ? "pop" : "ink"} mb-btn-wide mj-tier-btn`} onClick={() => onJoin(t.key, role)}>{label} <ArrowRight size={16} aria-hidden="true" /></button>}
          </motion.article>;
        })}
      </div>
      <ul className="mj-promises" aria-label="The small print">
        <li><Check size={16} aria-hidden="true" /> Monthly billing</li>
        <li><Check size={16} aria-hidden="true" /> Cancel anytime</li>
        <li><Check size={16} aria-hidden="true" /> Switch tiers whenever</li>
        <li><Check size={16} aria-hidden="true" /> Patterns &amp; supplies separate</li>
      </ul>
    </div>
  </section>;
}

/* ═══ A month in the club ═══ */
function Month() {
  return <section className="mj-sec mj-month" aria-labelledby="mj-month-title">
    <div className="b2-wrap">
      <Head id="mj-month-title" hand="a month in the" word="club" tone="blue" className="mb-headsm" />
      <ol className="mj-steps">
        {MONTH.map((s, i) => <motion.li key={s.when} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0, rotate: [-1.5, 1, -1, 1.5][i] }} viewport={VIEW} transition={{ delay: i * .1, duration: .5, ease: EASE }}>
          <span className="mj-step-n" aria-hidden="true">{i + 1}</span>
          <p className="mj-step-when">{s.when}</p>
          <h3>{s.what}</h3>
          <p>{s.text}</p>
        </motion.li>)}
      </ol>
      <svg className="mj-month-thread" viewBox="0 0 1200 60" preserveAspectRatio="none" aria-hidden="true"><Ink d="M10 40 C 200 0, 360 60, 600 30 S 1000 0, 1190 34" width={2.5} /></svg>
      <p className="b2-note mj-pj">pajamas encouraged</p>
    </div>
  </section>;
}

/* ═══ Moving from Patreon ═══ */
function Patreon() {
  return <section className="mj-sec mj-patreon" aria-labelledby="mj-patreon-title">
    <div className="b2-wrap">
      <div className="mj-patreon-top">
        <Head id="mj-patreon-title" hand="moving from" word="Patreon?" tone="blue" className="mb-headsm" />
        <p className="mj-proposal"><b>Proposal</b> · sample copy for team review</p>
      </div>
      <p className="mj-patreon-lede">The club is moving to its own home, right here. If you’re a patron now, switching takes two minutes and you keep everything.</p>
      <ol className="mj-patreon-steps">
        {PATREON_STEPS.map((s, i) => <motion.li key={s.t} className={`mj-note mj-note-${i}`} initial={{ opacity: 0, y: 40, rotate: 0 }} whileInView={{ opacity: 1, y: 0, rotate: [-2, 1.5, -1][i] }} viewport={VIEW} transition={{ delay: i * .12, type: "spring", stiffness: 120, damping: 16 }}>
          <i className={`b2-snap-tape b2-ktape-${["soft", "pop", "extra"][i]}`} aria-hidden="true" />
          <span className="mj-note-n">step {i + 1}</span>
          <h3>{s.t}</h3>
          <p>{s.d}</p>
        </motion.li>)}
      </ol>
      <ul className="mj-patreon-perks">
        {PATREON_PERKS.map(p => <li key={p}><Heart size={16} fill="currentColor" aria-hidden="true" /> {p}</li>)}
      </ul>
    </div>
  </section>;
}

/* ═══ FAQ ═══ */
function Faq() {
  const [open, setOpen] = useState<number | null>(0);
  const id = useId();
  return <section className="mj-sec mj-faq" aria-labelledby="mj-faq-title">
    <div className="b2-wrap mj-faq-grid">
      <div className="mj-faq-side">
        <Head id="mj-faq-title" hand="good" word="questions" tone="pink" className="mb-headsm" />
        <p className="b2-note mj-faq-note">still wondering? come ask me live on YouTube, any free Tuesday ♥</p>
      </div>
      <div className="mj-faq-list">
        {FAQS.map((f, i) => {
          const on = open === i;
          return <div key={f.q} className={`mj-qa${on ? " is-open" : ""}`}>
            <h3><button type="button" id={`${id}-q${i}`} aria-expanded={on} aria-controls={`${id}-a${i}`} onClick={() => setOpen(on ? null : i)}>
              <span>{f.q}</span><i aria-hidden="true">{on ? <Minus size={18} /> : <Plus size={18} />}</i>
            </button></h3>
            <div id={`${id}-a${i}`} role="region" aria-labelledby={`${id}-q${i}`} hidden={!on} className="mj-a"><p>{f.a}</p></div>
          </div>;
        })}
      </div>
    </div>
  </section>;
}

/* ═══ Last call ═══ */
function LastCall({ onJoin }: { onJoin: (t: Tier, role: Role) => void }) {
  const role = useRole();
  const to = useTo();
  const cta = joinCta(role);
  return <section className="mj-sec mj-last" aria-labelledby="mj-last-title">
    <div className="b2-wrap mj-last-grid">
      <motion.figure className="mj-last-snap" initial={{ opacity: 0, y: 30, rotate: 4 }} whileInView={{ opacity: 1, y: 0, rotate: -4 }} viewport={VIEW} transition={{ type: "spring", stiffness: 120, damping: 15 }}>
        <i className="b2-snap-tape b2-ktape-pop" aria-hidden="true" />
        <span><img src="/chelsea-and-cosmo.jpg" alt="Cosmo the cat on Chelsea’s lap at the sewing machine" /></span>
        <figcaption>Cosmo never misses a class <b aria-hidden="true">♥</b></figcaption>
      </motion.figure>
      <div className="mj-last-copy">
        <Head id="mj-last-title" hand="come sew" word="with me" tone="pink" className="mb-headsm" />
        <p className="mj-last-text">Your creative night in. Grab your fabric, settle in, and let’s make something you’re excited about.</p>
        <div className="mj-last-actions">
          {!cta ? <Link to={to(M.home)} className="mb-btn mb-btn-ink">Back to club home <ArrowRight size={16} aria-hidden="true" /></Link> : <>
            <button type="button" className="mb-btn mb-btn-ink" onClick={() => onJoin("member", role)}>{cta.label} <ArrowRight size={16} aria-hidden="true" /></button>
            {cta.secondary && <button type="button" className="mj-textbtn" onClick={() => onJoin("friend", role)}>{cta.secondary.label}</button>}
          </>}
        </div>
        <svg className="mj-last-heart" viewBox="0 0 30 46" aria-hidden="true"><Ink d={HEART} delay={.6} width={3.4} /></svg>
      </div>
    </div>
  </section>;
}

function Toast({ toast }: { toast: { msg: string; n: number } | null }) {
  return <div className="mj-toast-wrap" role="status" aria-live="polite">
    {toast && <motion.p key={toast.n} className="mj-toast" initial={{ opacity: 0, y: 16, scale: .96 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: .3, ease: EASE }}>
      <Sparkles size={16} aria-hidden="true" /> {toast.msg}
    </motion.p>}
  </div>;
}

/** The club's paywall and sales page. */
export function MembersJoin() {
  const { toast, show } = useToast();
  // Thread Friends upgrade and members switch down; everyone else checks out.
  const join = (t: Tier, role: Role) => {
    const plan = `${PLANS[t].name}, ${PLANS[t].per}`;
    show(role === "friend" && t === "member" ? `Upgrade would open here · ${plan}, and your $3 stops (prototype)`
      : role === "member" && t === "friend" ? `Plan change would open here · ${plan} from your next bill (prototype)`
      : `Checkout would open here · ${plan} (prototype)`);
  };
  return <MotionConfig reducedMotion="user">
    <MembersPage page="join" header={<Header onJoin={join} />}>
      <Part p="club"><Tiers onJoin={join} /></Part>
      <Part p="making"><Month /></Part>
      <Part p="words"><Patreon /></Part>
      <Part p="club"><Faq /></Part>
      <Part p="loop"><LastCall onJoin={join} /></Part>
      <Toast toast={toast} />
    </MembersPage>
  </MotionConfig>;
}
